import clsx from "clsx";
import type React from "react";
import { TITLE } from "shared/SharedConstants";

export type LogoVariant = "combined" | "emblem" | "wordmark" | "merch";
export type LogoTone = "black" | "current" | "white";

export type LogoSize = "sm" | "md" | "lg";

export interface LogoProps extends React.SVGProps<SVGSVGElement> {
  variant?: LogoVariant;
  tone?: LogoTone;
  /**
   * The logo is 1em tall. Omit `size` to follow the surrounding text, or pick a size for a header:
   * `sm` (20px) for sidebars, `md` (24px) for navigation bars, `lg` (28px) for sign-in pages.
   */
  size?: LogoSize;
}

const WORDMARK_GLYPHS = [
  {
    x: 0,
    d: "M387 -12Q282 -12 204 35Q126 81 83 166Q40 250 40 363Q40 476 84 561Q128 645 208 692Q288 739 393 739Q477 739 545 708Q612 677 657 621Q701 564 714 487H593Q574 555 520 593Q466 630 389 630Q319 630 267 598Q215 565 187 505Q158 445 158 364Q158 283 187 223Q216 163 268 130Q320 97 389 97Q466 97 522 135Q578 173 597 237H716Q701 162 656 106Q610 50 541 19Q472 -12 387 -12Z",
  },
  {
    x: 741,
    d: "M210 -13Q132 -13 88 29Q43 71 43 138Q43 206 91 247Q138 287 225 294L376 306V318Q376 356 362 379Q348 401 324 411Q300 421 268 421Q211 421 180 398Q149 375 149 333H57Q57 386 84 425Q110 463 159 485Q208 506 273 506Q337 506 384 484Q430 461 456 416Q481 371 481 303V0H388L378 76Q361 36 315 12Q269 -13 210 -13ZM244 70Q305 70 341 107Q377 143 377 205V232L263 222Q203 217 178 196Q152 175 152 143Q152 106 176 88Q200 70 244 70Z",
  },
  { x: 1282, d: "M175 0H67V740H175Z" },
  {
    x: 1524,
    d: "M173 0H66V491H164L177 402L164 413Q179 451 220 479Q260 506 319 506Q384 506 426 472Q468 437 483 380H464Q475 437 521 472Q566 506 631 506Q718 506 766 457Q814 407 814 321V0H707V294Q707 349 680 379Q652 408 605 408Q573 408 548 394Q523 380 509 353Q494 325 494 285V0H387V295Q387 350 360 379Q332 408 285 408Q253 408 228 394Q202 379 188 352Q173 325 173 285Z",
  },
  { x: 2398, d: "M196 727V0H83V727ZM102 0V104H507V0Z" },
  {
    x: 2918,
    d: "M282 -13Q209 -13 154 20Q98 53 67 112Q35 170 35 246Q35 323 66 381Q96 439 151 473Q206 506 278 506Q350 506 403 476Q455 445 485 390Q514 334 514 259V223L92 222L94 292H406Q406 349 372 383Q337 416 278 416Q233 416 202 397Q170 378 154 341Q137 303 137 249Q137 165 175 121Q212 77 284 77Q336 77 370 97Q404 117 414 156H515Q498 77 437 32Q375 -13 282 -13Z",
  },
  {
    x: 3468,
    d: "M173 0H66V491H164L174 422Q198 462 241 484Q283 506 335 506Q429 506 476 451Q522 396 522 302V0H415V277Q415 344 385 376Q354 408 303 408Q242 408 208 369Q173 329 173 263Z",
  },
  {
    x: 4044,
    d: "M27 146H129Q130 111 155 91Q180 71 224 71Q270 71 295 89Q320 107 320 136Q320 158 307 173Q294 187 258 196L170 216Q105 232 73 265Q40 298 40 355Q40 402 65 436Q89 469 132 488Q175 506 230 506Q285 506 326 487Q367 467 390 432Q413 397 413 349H311Q310 384 288 404Q266 424 226 424Q187 424 165 407Q143 389 143 359Q143 314 210 297L298 277Q360 263 391 232Q422 200 422 144Q422 97 397 61Q371 25 326 6Q281 -13 221 -13Q132 -13 80 31Q27 75 27 146Z",
  },
] as const;

const Emblem = () => (
  <>
    <path d="M12 48a38 38 0 0 1 76 0H76a26 26 0 0 0-52 0H12Z" />
    <circle cx="50" cy="38" r="8" />
    <path
      d="M10 56h80C87 78 70 92 50 92S13 78 10 56Zm15 11c5 9 14 14 25 14s20-5 25-14H25Z"
      fillRule="evenodd"
    />
  </>
);

const Wordmark = () => (
  <>
    {WORDMARK_GLYPHS.map((glyph) => (
      <path key={glyph.x} transform={`translate(${glyph.x})`} d={glyph.d} />
    ))}
  </>
);

const toneColors: Record<LogoTone, string | undefined> = {
  black: "#000000",
  current: undefined,
  white: "#ffffff",
};

const sizeClasses: Record<LogoSize, string> = {
  sm: "text-[1.25rem]",
  md: "text-[1.5rem]",
  lg: "text-[1.75rem]",
};

const Logo = (props: LogoProps) => {
  const {
    variant = "combined",
    tone = "current",
    size,
    className,
    style,
    "aria-label": ariaLabel = TITLE,
    ...otherProps
  } = props;
  const viewBox =
    variant === "merch"
      ? "0 0 644 76"
      : variant === "emblem"
        ? "0 0 100 100"
        : variant === "wordmark"
          ? "0 0 450 76"
          : "0 0 496 76";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      aria-label={ariaLabel}
      className={clsx(
        "inline-block h-[1em] w-auto shrink-0 align-[-0.08em]",
        size && sizeClasses[size],
        className,
      )}
      fill="currentColor"
      focusable="false"
      preserveAspectRatio="xMinYMid meet"
      role="img"
      style={{ color: toneColors[tone], ...style }}
      viewBox={viewBox}
      {...otherProps}
    >
      {variant === "emblem" ? (
        <Emblem />
      ) : variant === "wordmark" ? (
        <g transform="translate(0 74) scale(.1 -.1)">
          <Wordmark />
        </g>
      ) : (
        <>
          <g transform="translate(0 -9) scale(.92)">
            <Emblem />
          </g>
          <g transform="translate(104 69) scale(.0865 -.0865)">
            <Wordmark />
          </g>
        </>
      )}
      {variant === "merch" ? (
        <path
          transform="translate(4 0)"
          opacity={0.8}
          d="M499.22 69.41L499.22 69.41Q497.47 69.41 496.23 68.17Q495 66.94 495 65.19L495 65.19Q495 63.44 496.23 62.20Q497.47 60.97 499.22 60.97L499.22 60.97Q500.97 60.97 502.20 62.20Q503.44 63.44 503.44 65.19L503.44 65.19Q503.44 66.94 502.20 68.17Q500.97 69.41 499.22 69.41Z M525.59 69.75L525.59 69.75Q520.84 69.75 517.27 67.50Q513.69 65.25 511.69 61.19Q509.69 57.13 509.69 51.75L509.69 51.75Q509.69 46.28 511.69 42.20Q513.69 38.13 517.27 35.88Q520.84 33.63 525.59 33.63L525.59 33.63Q528.13 33.63 530.38 34.28Q532.63 34.94 534.48 36.17Q536.34 37.41 537.69 39.20Q539.03 41 539.75 43.28L539.75 43.28L534.34 44.81Q533.97 43.47 533.19 42.34Q532.41 41.22 531.30 40.39Q530.19 39.56 528.75 39.11Q527.31 38.66 525.59 38.66L525.59 38.66Q522.09 38.66 519.84 40.48Q517.59 42.31 516.50 45.28Q515.41 48.25 515.41 51.75L515.41 51.75Q515.41 55.19 516.50 58.14Q517.59 61.09 519.84 62.91Q522.09 64.72 525.59 64.72L525.59 64.72Q527.34 64.72 528.81 64.25Q530.28 63.78 531.42 62.92Q532.56 62.06 533.34 60.88Q534.13 59.69 534.50 58.28L534.50 58.28L539.88 59.81Q539.19 62.16 537.83 63.98Q536.47 65.81 534.59 67.11Q532.72 68.41 530.44 69.08Q528.16 69.75 525.59 69.75Z M560.16 69.75L560.16 69.75Q555.41 69.75 551.83 67.50Q548.25 65.25 546.25 61.19Q544.25 57.13 544.25 51.75L544.25 51.75Q544.25 46.28 546.25 42.20Q548.25 38.13 551.83 35.88Q555.41 33.63 560.16 33.63L560.16 33.63Q564.94 33.63 568.53 35.88Q572.13 38.13 574.13 42.20Q576.13 46.28 576.13 51.75L576.13 51.75Q576.13 57.13 574.13 61.19Q572.13 65.25 568.53 67.50Q564.94 69.75 560.16 69.75ZM560.16 64.72L560.16 64.72Q563.69 64.72 565.97 62.91Q568.25 61.09 569.34 58.14Q570.44 55.19 570.44 51.75L570.44 51.75Q570.44 48.28 569.34 45.30Q568.25 42.31 565.97 40.48Q563.69 38.66 560.16 38.66L560.16 38.66Q556.66 38.66 554.41 40.48Q552.16 42.31 551.06 45.28Q549.97 48.25 549.97 51.75L549.97 51.75Q549.97 55.19 551.06 58.14Q552.16 61.09 554.41 62.91Q556.66 64.72 560.16 64.72Z M587.94 69L582.31 69L582.31 34.06L587.78 34.06L587.81 41.88L587.38 41.88Q588.09 39.03 589.64 37.17Q591.19 35.31 593.27 34.38Q595.34 33.44 597.59 33.44L597.59 33.44Q601.41 33.44 603.77 35.78Q606.13 38.13 606.81 41.94L606.81 41.94L606.06 41.94Q606.69 39.31 608.34 37.41Q610 35.50 612.36 34.47Q614.72 33.44 617.50 33.44L617.50 33.44Q620.53 33.44 623.03 34.75Q625.53 36.06 627.02 38.72Q628.50 41.38 628.50 45.44L628.50 45.44L628.50 69L622.88 69L622.88 45.59Q622.88 41.81 620.75 40.19Q618.63 38.56 615.81 38.56L615.81 38.56Q613.47 38.56 611.77 39.55Q610.06 40.53 609.14 42.25Q608.22 43.97 608.22 46.19L608.22 46.19L608.22 69L602.59 69L602.59 45.03Q602.59 42.13 600.66 40.34Q598.72 38.56 595.75 38.56L595.75 38.56Q593.72 38.56 591.92 39.52Q590.13 40.47 589.03 42.34Q587.94 44.22 587.94 47L587.94 47L587.94 69Z"
        />
      ) : null}
    </svg>
  );
};

export default Logo;
